import { Button, Form, IFormRender } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { Panel } from 'components';
import DualPanel from 'components/foundation/panels/DualPanel/DualPanel';
import { ReactNode, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { ClassName } from 'types/ClassName';
import { t } from 'utils/localize';
import { validateEmail } from 'utils/stringUtils';
import OnboardingInformationPanelContent, {
  IOnboardingInformationPanelContentProps,
} from '../OnboardingInformationPanelContent/OnboardingInformationPanelContent';
import './OnboardingFormCoordinator.scss';

export interface IOnboardingFormCoordinatorProps<
  FormType extends {
    [field: string]: any;
  },
> {
  defaultHeader: string;
  defaultBody: string;
  formFields: IOnboardingFormField[];
  formButtonLabel: string;
  formData: FormType;
  onSubmit: (formData: FormType) => void;
  formFooter?: ReactNode;
  shouldRenderLeftContent?: boolean;
  onSubmissionFailure?: (formData: FormType) => void;
  dialogFormData?: { usernameChoice: string };
  className?: ClassName;
  disableCaptcha?: boolean;
}

export interface IOnboardingFormField {
  renderField: (form: any, onFocus: () => void, onBlur: () => void) => ReactNode;
  panelContent: IOnboardingInformationPanelContentProps;
  ref?: HTMLElement;
}

function OnboardingFormCoordinator<
  FormType extends {
    [field: string]: any;
  },
>(props: IOnboardingFormCoordinatorProps<FormType>) {
  const {
    defaultHeader,
    defaultBody,
    formFields,
    formData,
    formButtonLabel,
    onSubmit,
    formFooter,
    className,
    onSubmissionFailure,
    shouldRenderLeftContent,
    dialogFormData,
    disableCaptcha = false,
  } = props;

  const [currentFocusedField, setCurrentFocusedField] = useState(-1);
  const panelClicked = useRef(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  function renderLeftContent() {
    function onPanelMouseDown() {
      panelClicked.current = true;
    }

    if (currentFocusedField === -1) {
      return (
        <OnboardingInformationPanelContent
          onMouseDown={onPanelMouseDown}
          headerText={defaultHeader}
          bodyText={defaultBody}
          isTitle={true}
        />
      );
    } else {
      return (
        <OnboardingInformationPanelContent
          onMouseDown={onPanelMouseDown}
          {...formFields[currentFocusedField].panelContent}
        />
      );
    }
  }

  function onBlur() {
    if (!panelClicked.current) {
      setCurrentFocusedField(-1);
    }

    panelClicked.current = false;
  }

  function formRender(form: IFormRender<any>) {
    const handleRecaptchaChange = (token: string | null) => {
      if (token) {
        form.state.data.token = token;

        const didSubmit = form.handleSubmit();

        if (didSubmit === false && onSubmissionFailure && validateEmail(form.state.data.email)) {
          if (dialogFormData?.usernameChoice !== 'separateUsername') {
            form.clearIsDirty();
          }
          onSubmissionFailure(form.state.data);
        }
      }
    };

    function handleOnClick() {
      if (disableCaptcha) {
        handleRecaptchaChange('test-token');
      } else if (recaptchaRef.current) {
        recaptchaRef.current.execute();
      }
    }

    function onFormSubmitMouseDown(e: any) {
      e.preventDefault();
    }

    return (
      <>
        {formFields.map((field, index) => {
          function onFocus() {
            setCurrentFocusedField(index);
          }
          return field.renderField(form, onFocus, onBlur);
        })}

        {!disableCaptcha && (
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? ''}
            size="invisible"
            onChange={handleRecaptchaChange}
          />
        )}

        <Button className={'form-button'} type={'primary'} onMouseDown={onFormSubmitMouseDown} onClick={handleOnClick}>
          {formButtonLabel}
        </Button>

        {formFooter}
      </>
    );
  }

  function renderForm() {
    return <Form data={formData} onSubmit={onSubmit} render={formRender} allowMultipleSubmissions={true} />;
  }

  function renderMobileHeader() {
    let headerTitle = defaultHeader;
    switch (defaultHeader) {
      case t('Company'):
        headerTitle = t('COMPANY INFORMATION');
        break;
      case t('Account'):
        headerTitle = t('YOUR ACCOUNT');
        break;
      case t('Profile'):
        headerTitle = t('YOUR PERSONAL PROFILE');
        break;
    }
    return headerTitle === 'Payroll Settings' ||
      headerTitle === 'Edit Pay Period' ||
      headerTitle === 'Edit Pay Period History' ? (
      <></>
    ) : (
      <div className="mobile-header">
        <span className={'mobile-header-span'}>{headerTitle}</span>
      </div>
    );
  }

  const classes = classNames(
    {
      'onboarding-form-coordinator': true,
    },
    className
  );

  return (
    <>
      {renderMobileHeader()}
      {shouldRenderLeftContent == false ? (
        <Panel className={classes}>{renderForm()}</Panel>
      ) : (
        <DualPanel className={classes} leftContent={renderLeftContent} rightContent={renderForm} />
      )}
    </>
  );
}

export default OnboardingFormCoordinator;
